import type { LocalDate } from "@js-joda/core";
import { WorkerEmploymentType } from "model/Salary";
import { Api } from "services/api/api.service";
import type { BackendAxiosError } from "services/api/backendError";
import type {
  StaffProfileData,
  StaffProfileResponseV3,
} from "services/api/company/staff.service";
import type { EmploymentDetailsForm } from "../profilePanel.contract";
import { resolveStaffProfilePanelErrorMessage } from "../domains/employment/profilePanel.error.logic";
import type { EmploymentUiRules } from "../../shared/staffEmploymentRules.logic";
import {
  buildUpdateStaffWorkerPayload,
  toLocalDateOrNull,
} from "../domains/employment/profilePanel.payload.logic";
import { toEmploymentDetailsForm } from "./profilePanel.state.formMappers.logic";

interface PerformSaveEmploymentParams {
  companyId: number | null;
  workerId: number | null;
  profile: StaffProfileData | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  editingTab: "employmentSalary" | "profile" | null;
  employmentRules: EmploymentUiRules;
  startDate: LocalDate | null;
  endDate: LocalDate | null;
  translate: (key: string, options?: Record<string, unknown>) => string;
  setIsSaving: (value: boolean) => void;
  setSaveError: (value: string) => void;
  setEditingTab: (value: null) => void;
  setModalState: (value: { type: "none" }) => void;
  applySavedProfile: (savedProfile: StaffProfileResponseV3) => void;
  onWorkerUpdated?: () => void;
}

export const performSaveEmployment = (
  params: PerformSaveEmploymentParams,
): void => {
  if (params.editingTab !== "employmentSalary") return;
  if (params.companyId == null || params.workerId == null) return;

  if (!params.startDate) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  if (params.employmentRules.isEndDateRequiredOnSave && !params.endDate) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const selectedEmployment = params.profile?.employmentInfo?.selected;
  const selectedEmploymentId = selectedEmployment?.id;
  if (selectedEmploymentId == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const payloadParams = {
    editedEmploymentDetails: params.editedEmploymentDetails,
    effectiveFromDate: params.startDate,
    effectiveEndDate: params.endDate,
  };
  const companyId = params.companyId;
  const workerId = params.workerId;

  params.setIsSaving(true);
  params.setSaveError("");

  Api()
    .company.staff.updateWorkerEmployment(
      companyId,
      workerId,
      selectedEmploymentId,
      buildUpdateStaffWorkerPayload(payloadParams).payload,
    )
    .then((response) => {
      params.applySavedProfile(response.data);
      params.setEditingTab(null);
      params.setModalState({ type: "none" });
      params.onWorkerUpdated?.();
    })
    .catch((error: BackendAxiosError) => {
      params.setSaveError(
        resolveStaffProfilePanelErrorMessage(error, params.translate),
      );
    })
    .finally(() => {
      params.setIsSaving(false);
    });
};

interface ChangeIntroductoryToPermanentParams {
  readonly companyId: number | null;
  readonly profile: StaffProfileData | null;
  readonly workerId: number | null;
  readonly closeModal: () => void;
  readonly onWorkerUpdated?: () => void;
  readonly reloadProfile: () => void;
  readonly setIsSaving: (value: boolean) => void;
  readonly setSaveError: (value: string) => void;
  readonly translate: (
    key: string,
    options?: Record<string, unknown>,
  ) => string;
}

export function changeIntroductoryToPermanent(
  params: ChangeIntroductoryToPermanentParams,
): Promise<void> {
  const selectedEmployment = params.profile?.employmentInfo?.selected;
  const startDate = toLocalDateOrNull(selectedEmployment?.startDate);

  if (
    params.companyId == null ||
    params.workerId == null ||
    selectedEmployment?.id == null ||
    selectedEmployment.employmentType !== WorkerEmploymentType.INTRODUCTORY ||
    startDate == null
  ) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return Promise.resolve();
  }

  const editedEmploymentDetails: EmploymentDetailsForm = {
    ...toEmploymentDetailsForm(params.profile),
    employmentType: WorkerEmploymentType.EMPLOYEE,
    endDate: null,
  };
  const payload = buildUpdateStaffWorkerPayload({
    editedEmploymentDetails,
    effectiveFromDate: startDate,
    effectiveEndDate: null,
  }).payload;
  const companyId = params.companyId;
  const workerId = params.workerId;
  const employmentId = selectedEmployment.id;

  params.setIsSaving(true);
  params.setSaveError("");

  return Api()
    .company.staff.updateWorkerEmployment(
      companyId,
      workerId,
      employmentId,
      payload,
    )
    .then(() => {
      params.closeModal();
      params.reloadProfile();
      params.onWorkerUpdated?.();
    })
    .catch((error: BackendAxiosError) => {
      params.setSaveError(
        resolveStaffProfilePanelErrorMessage(error, params.translate),
      );
    })
    .finally(() => {
      params.setIsSaving(false);
    });
}
